import { useTranslation } from "react-i18next";
import UiButton from "../../components/button/Button";
import { PostDischargeRow } from "../../models/pages/post-discharge-modal/post-discharge-modal";
import UiSelect from "../../components/select/Select.component";
import { Formatter } from "../formatter/formatter.service";
import { PostDischargeProcedureType, ProcedureStatus } from "../../models/procedures.model";
import { careTransitionTypeOptions } from "../../utils/care-transitions";
import UiInputTextarea from "../../components/input-textarea/InputTextarea.component";
import { Validations } from "../form/validations.service";

// Template: Actions (Edit and Delete)
const templateActions = (
	row: PostDischargeRow,
	editableFn: (row: PostDischargeRow) => void,
	cancelFn: (row: PostDischargeRow) => void,
	submitting: boolean,
	deleteFn: (row: PostDischargeRow) => void
): React.ReactNode => {
	const isEditing = row.editing;
	if (isEditing) {
		return (
			<>
				<UiButton
					type="button"
					className="p-button-sm p-button-outlined p-button-rounded"
					label="UI_COMPONENTS.BUTTONS.CANCEL"
					onClick={cancelFn}
					disabled={submitting}
					style={{
						marginRight: 5,
					}}
				/>
				<UiButton
					type="submit"
					className="p-button-sm p-button-rounded"
					label={
						submitting ? "UI_COMPONENTS.BUTTONS.SAVING" : "UI_COMPONENTS.BUTTONS.SAVE"
					}
					loading={submitting}
					disabled={submitting}
				/>
			</>
		);
	}

	return (
		<>
			{
				<UiButton
					key="edit-biometric"
					type="button"
					icon="pi pi-pencil"
					className="p-button-text neutral"
					onClick={() => editableFn(row)}
					style={{
						marginRight: 0,
					}}
				/>
			}
			{
				<UiButton
					type="button"
					key="delete-biometric"
					icon={"pi pi-trash"}
					className="p-button-text neutral"
					onClick={() => deleteFn(row)}
				/>
			}
		</>
	);
};

// Column: status
const templateStatus = (
	row: PostDischargeRow,
	updateFn: (value: ProcedureStatus) => void,
	submitting: boolean
): React.ReactNode => {
	const { t } = useTranslation("common"); // eslint-disable-line

	let template: React.ReactNode;

	if (row.editing) {
		template = (
			<UiSelect
				id="status"
				name="status"
				validations={[Validations.required]}
				removeBottomSpacer={true}
				style={{ verticalAlign: "middle" }}
				value={t(`ENUMS.PROCEDURES_STATUS.${row.status}`)}
				onChange={(e) => updateFn(e.value)}
				disabled={submitting}
				options={[
					{
						id: "SCHEDULED",
						label: "ENUMS.PROCEDURES_STATUS.SCHEDULED",
					},
					{
						id: "COMPLETED",
						label: "ENUMS.PROCEDURES_STATUS.COMPLETED",
					},
				]}
			/>
		);
	} else {
		template = <span className="text">{t(`ENUMS.PROCEDURES_STATUS.${row.status}`)}</span>;
	}

	return template;
};

// Column: status
const templatePostDischargeType = (
	row: PostDischargeRow,
	updateFn: (value: PostDischargeProcedureType) => void,
	submitting: boolean
): React.ReactNode => {
	const { t } = useTranslation("common"); // eslint-disable-line

	let template: React.ReactNode;

	if (row.editing) {
		template = (
			<UiSelect
				id="type"
				name="type"
				validations={[Validations.required]}
				removeBottomSpacer={true}
				value={t(`ENUMS.POST_DISCHARGE_PROCEDURES_TYPES.${row.type}`)}
				onChange={(e) => updateFn(e.value)}
				disabled={submitting}
				options={careTransitionTypeOptions()}
			/>
		);
	} else {
		template = (
			<span className="text">{t(`ENUMS.POST_DISCHARGE_PROCEDURES_TYPES.${row.type}`)}</span>
		);
	}

	return template;
};

// Template: notes
const templateNotes = (
	row: PostDischargeRow,
	updateFn: (value: string) => void,
	submitting: boolean
): React.ReactNode => {
	let template = undefined;
	if (row.editing) {
		template = (
			<UiInputTextarea
				id="notes"
				name="notes"
				removeBottomSpacer={true}
				placeholder="PARTICIPANT.POST_DISCHARGE.MODAL.reason_placeholder"
				value={row.notes || ""}
				onChange={(e) => updateFn(e.currentTarget.value)}
				disabled={submitting}
			/>
		);
	} else {
		template = <span className="text">{row.notes ? row.notes : Formatter.NO_DATA}</span>;
	}
	return template;
};

/**
 * COLUMNS WIDTH
 */

const columnsWidth = {
	dateOn: "100px",
	status: "100px",
	reason: "150px",
	edit: "150px",
};

export const PostDischargeTemplate = {
	templateActions,
	templateStatus,
	templateNotes,
	templatePostDischargeType,
	columnsWidth,
};
